/**
 * ComplianceDiffPage — visualize what changed between two scans of the
 * same (network, preset).
 *
 * Driven by `GET /api/compliance/scans/{a}/diff/{b}`. The backend joins
 * the per-pointer ComplianceResults of A (newer) and B (older) and
 * buckets them into:
 *   - regressed:  newly drifted in A    → rose, surfaced first (most urgent)
 *   - persistent: still drifted in both → peach, the "still broken" set
 *   - fixed:      drift cleared in A    → mint, the "we improved" set
 *   - onlyInA / onlyInB: pointer present on one side only — rare, surfaced
 *     in a collapsible footer because a stable preset shouldn't move pointers
 *
 * Page route: /compliance/diff/:aId/:bId. Driven from the recent-scans
 * rail's "Compare to previous" link.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeftRight,
  CheckCircle2,
  ChevronRight,
  Minus,
  ShieldAlert,
  ShieldCheck,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardSub, CardTitle } from "@/components/ui/card";
import { PanelSkeleton } from "@/components/ui/skeleton";
import { useCompliance } from "@/stores/compliance";
import type { ScanDiffEntry } from "@/api/schemas/compliance";
import { cn } from "@/lib/cn";
import { useUiAutoAnimate } from "@/components/ui/auto-animate";

/** Same compact-JSON helper as /compliance — keeps the side-by-side
 *  desired/current cells tight when the values are large objects. */
function compact(value: unknown, max = 120): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string")
    return value.length > max ? value.slice(0, max - 1) + "…" : value;
  try {
    const s = JSON.stringify(value);
    return s.length > max ? s.slice(0, max - 1) + "…" : s;
  } catch {
    return String(value);
  }
}

function formatRelative(iso: string): string {
  const norm = iso.includes("T") ? iso : iso.replace(" ", "T") + "Z";
  const t = new Date(norm).getTime();
  if (Number.isNaN(t)) return "—";
  const sec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 48) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

export function ComplianceDiffPage() {
  const animatedRootRef = useUiAutoAnimate();

  const { aId = "", bId = "" } = useParams();
  const diff = useCompliance((s) => s.diff);
  const diffStatus = useCompliance((s) => s.diffStatus);
  const diffError = useCompliance((s) => s.diffError);
  const loadDiff = useCompliance((s) => s.loadDiff);
  const clearDiff = useCompliance((s) => s.clearDiff);

  useEffect(() => {
    if (aId && bId) void loadDiff(aId, bId);
    return () => clearDiff();
  }, [aId, bId, loadDiff, clearDiff]);

  // Roll-up counts that drive the hero stats. Computed off the diff once
  // it's loaded; null while still fetching.
  const stats = useMemo(() => {
    if (!diff) return null;
    return {
      regressed: diff.regressed.length,
      fixed: diff.fixed.length,
      persistent: diff.persistent.length,
      onlyInA: diff.onlyInA.length,
      onlyInB: diff.onlyInB.length,
    };
  }, [diff]);

  if (diffStatus === "loading" || (!diff && diffStatus !== "error")) {
    return (
      <div ref={animatedRootRef} className="flex flex-col gap-6">
        <div className="flex items-center gap-2 text-xs text-fg-mute">
          <Link to="/compliance" className="text-fg-dim hover:text-fg">
            Compliance
          </Link>
          <span className="opacity-40">/</span>
          <span className="text-fg">Compare scans</span>
        </div>
        <PanelSkeleton />
      </div>
    );
  }

  if (diffError) {
    return (
      <div ref={animatedRootRef} className="flex flex-col gap-6">
        <div className="flex items-center gap-2 text-xs text-fg-mute">
          <Link to="/compliance" className="text-fg-dim hover:text-fg">
            Compliance
          </Link>
          <span className="opacity-40">/</span>
          <span className="text-fg">Compare scans</span>
        </div>
        <output
          className="flex items-center gap-2.5 rounded-xl border border-rose/30 bg-rose/10 px-3.5 py-2.5 text-sm text-rose"
        >
          <AlertTriangle size={16} />
          <span>
            <b className="font-semibold">Could not load diff.</b> {diffError}
          </span>
        </output>
      </div>
    );
  }

  if (!diff || !stats) return null;

  const overallTrend =
    stats.regressed > stats.fixed
      ? "worse"
      : stats.fixed > stats.regressed
        ? "better"
        : "flat";

  return (
    <div ref={animatedRootRef} className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">
          Operations
        </Link>
        <span className="opacity-40">/</span>
        <Link to="/compliance" className="text-fg-dim hover:text-fg">
          Compliance
        </Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">Compare scans</span>
      </div>

      {/* Hero — the two scans side by side with a centered ↔ separator */}
      <div className="grid grid-cols-1 items-stretch gap-3 sm:grid-cols-[1fr_auto_1fr]">
        <ScanHeadCard label="Newer (A)" head={diff.a} />
        <div className="flex items-center justify-center text-fg-mute">
          <ArrowLeftRight size={20} />
        </div>
        <ScanHeadCard label="Older (B)" head={diff.b} />
      </div>

      {/* Banner if the diff spans different network/preset (sanity check) */}
      {diff.warnings.length > 0 ? (
        <output
          className="flex items-center gap-2.5 rounded-xl border border-peach/30 bg-peach/10 px-3.5 py-2.5 text-sm text-peach"
        >
          <AlertTriangle size={16} />
          <span>
            <b className="font-semibold">Diff spans different scopes.</b>{" "}
            {diff.warnings.join(" · ")}
          </span>
        </output>
      ) : null}

      {/* Top-level KPIs — verdict at a glance */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KpiTile
          label="Regressed (new)"
          value={stats.regressed}
          tone="err"
          Icon={TrendingUp}
        />
        <KpiTile
          label="Fixed"
          value={stats.fixed}
          tone="ok"
          Icon={TrendingDown}
        />
        <KpiTile
          label="Still drifted"
          value={stats.persistent}
          tone="warn"
          Icon={ShieldAlert}
        />
        <KpiTile
          label="Net change"
          value={Math.abs(stats.regressed - stats.fixed)}
          tone={
            overallTrend === "worse"
              ? "err"
              : overallTrend === "better"
                ? "ok"
                : "neutral"
          }
          Icon={
            overallTrend === "worse"
              ? TrendingUp
              : overallTrend === "better"
                ? TrendingDown
                : Minus
          }
          suffix={
            overallTrend === "worse"
              ? "worse"
              : overallTrend === "better"
                ? "better"
                : "flat"
          }
        />
      </div>

      {/* Sections — regressed first (most urgent), then persistent, then fixed */}
      {stats.regressed > 0 ? (
        <Section
          title={`${stats.regressed} new drift`}
          subtitle="These pointers were compliant in B but now non-compliant in A — regressions."
          tone="err"
          entries={diff.regressed}
          showASide
        />
      ) : null}
      {stats.persistent > 0 ? (
        <Section
          title={`${stats.persistent} still drifted`}
          subtitle="Non-compliant in both runs — the rules that have been broken since at least B."
          tone="warn"
          entries={diff.persistent}
          showASide
        />
      ) : null}
      {stats.fixed > 0 ? (
        <Section
          title={`${stats.fixed} fixed`}
          subtitle="Were non-compliant in B; compliant in A — improvements."
          tone="ok"
          entries={diff.fixed}
          showASide={false}
        />
      ) : null}
      {stats.regressed === 0 && stats.persistent === 0 && stats.fixed === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
            <ShieldCheck size={28} className="text-mint" />
            <CardTitle>No drift differences</CardTitle>
            <CardSub>
              Both scans are clean (or share the same set of compliant entries).
              Nothing changed.
            </CardSub>
          </CardContent>
        </Card>
      ) : null}

      {/* Pointer-set differences — usually empty (preset is stable). Collapsed
          by default to keep the page short. */}
      {stats.onlyInA + stats.onlyInB > 0 ? (
        <Card>
          <CardContent className="flex flex-col gap-2 py-3">
            <CardSub>
              {stats.onlyInA > 0 ? (
                <>
                  {stats.onlyInA} pointer{stats.onlyInA === 1 ? "" : "s"} only
                  in A ·{" "}
                </>
              ) : null}
              {stats.onlyInB > 0 ? (
                <>
                  {stats.onlyInB} pointer{stats.onlyInB === 1 ? "" : "s"} only
                  in B ·{" "}
                </>
              ) : null}
              <span className="text-fg-mute">
                preset shape changed between runs (rare unless someone edited
                the fixture)
              </span>
            </CardSub>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

function ScanHeadCard({
  label,
  head,
}: {
  label: string;
  head:
    | import("@/api/schemas/compliance").ComplianceScanFull
    | {
        id: string;
        ranAt: string;
        ranBy: string;
        networkId: string;
        networkName?: string | null;
        presetSlug: string;
        processName: string;
        summary: { compliant: number; "non-compliant": number; ignore: number };
        durationMs?: number | null;
        source: string;
      };
}) {
  const drift = head.summary["non-compliant"] ?? 0;
  return (
    <Card>
      <CardContent className="flex flex-col gap-1 py-3">
        <div className="flex items-center justify-between gap-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
            {label}
          </span>
          <Badge variant={drift > 0 ? "err" : "ok"} size="sm" dot>
            {drift > 0 ? `${drift} drift` : "clean"}
          </Badge>
        </div>
        <CardTitle className="truncate text-base">{head.processName}</CardTitle>
        <div className="font-mono text-[11px] text-fg-mute">
          {head.networkName ?? head.networkId} ·{" "}
          <span title={head.ranAt}>{formatRelative(head.ranAt)}</span> ·{" "}
          {head.source}
        </div>
        <div className="font-mono text-[10.5px] text-fg-dim">
          ran by <b className="text-fg">{head.ranBy}</b>
          {head.durationMs ? <> · {head.durationMs}ms</> : null}
        </div>
      </CardContent>
    </Card>
  );
}

function KpiTile({
  label,
  value,
  tone,
  Icon,
  suffix,
}: {
  label: string;
  value: number;
  tone: "ok" | "err" | "warn" | "neutral";
  Icon: typeof ShieldCheck;
  suffix?: string;
}) {
  const tint =
    tone === "ok"
      ? "bg-mint/15 text-mint"
      : tone === "err"
        ? "bg-rose/15 text-rose"
        : tone === "warn"
          ? "bg-peach/15 text-peach"
          : "bg-aurora-soft text-violet";
  return (
    <Card>
      <CardContent className="flex items-center gap-3 py-3">
        <div
          className={cn(
            "flex h-9 w-9 items-center justify-center rounded-lg",
            tint,
          )}
        >
          <Icon size={15} />
        </div>
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
            {label}
          </div>
          <div className="text-xl font-extrabold text-fg">
            {value.toLocaleString()}
            {suffix ? (
              <span className="ml-1.5 font-mono text-[11px] text-fg-mute uppercase">
                {suffix}
              </span>
            ) : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Section({
  title,
  subtitle,
  tone,
  entries,
  showASide,
}: {
  title: string;
  subtitle: string;
  tone: "ok" | "err" | "warn";
  entries: ScanDiffEntry[];
  /** Whether the "current" panel (A) or the "previous" panel (B) carries
   *  the still-non-compliant value worth showing. For "regressed" and
   *  "persistent" we want A; for "fixed" we want B (showing the rule
   *  that was broken). */
  showASide: boolean;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline gap-2">
        <CardTitle className="text-base">{title}</CardTitle>
        <CardSub>{subtitle}</CardSub>
      </div>
      <div className="flex flex-col gap-2">
        {entries.map((e) => (
          <DiffEntryRow
            key={e.pointer}
            entry={e}
            tone={tone}
            showASide={showASide}
          />
        ))}
      </div>
    </div>
  );
}

function DiffEntryRow({
  entry,
  tone,
  showASide,
}: {
  entry: ScanDiffEntry;
  tone: "ok" | "err" | "warn";
  showASide: boolean;
}) {
  const [open, setOpen] = useState(false);
  const focal = showASide ? entry.a : entry.b;
  const errors = focal?.errors ?? [];
  const tintBorder =
    tone === "ok"
      ? "border-mint/30 bg-mint/5"
      : tone === "err"
        ? "border-rose/30 bg-rose/5"
        : "border-peach/30 bg-peach/5";
  const Icon =
    tone === "ok" ? CheckCircle2 : tone === "err" ? ShieldAlert : ShieldAlert;
  return (
    <Card className={cn("border", tintBorder)}>
      <CardContent className="flex flex-col gap-2 py-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 text-left"
        >
          <Icon
            size={11}
            className={cn(
              tone === "ok" && "text-mint",
              tone === "err" && "text-rose",
              tone === "warn" && "text-peach",
            )}
          />
          <span className="flex-1 truncate font-mono text-[12px] text-fg">
            {entry.pointer}
          </span>
          {errors[0] ? (
            <span className="hidden truncate text-[11px] text-fg-mute md:inline-block max-w-[40%]">
              {errors[0]}
            </span>
          ) : null}
          <ChevronRight
            size={12}
            className={cn(
              "shrink-0 text-fg-mute transition-transform",
              open && "rotate-90",
            )}
          />
        </button>
        {open ? (
          <div className="flex flex-col gap-2 border-t border-line/60 pt-2 text-[11.5px]">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <DiffSide label="A — newer" result={entry.a} />
              <DiffSide label="B — older" result={entry.b} />
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function DiffSide({
  label,
  result,
}: {
  label: string;
  result: import("@/api/schemas/compliance").ComplianceResult | undefined;
}) {
  if (!result) {
    return (
      <div className="rounded-md border border-line bg-surface px-2.5 py-1.5">
        <div className="mb-0.5 font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
          {label}
        </div>
        <div className="text-fg-mute">— not present —</div>
      </div>
    );
  }
  const verdictTint =
    result.status === "compliant"
      ? "border-mint/30 bg-mint/5"
      : result.status === "non-compliant"
        ? "border-rose/30 bg-rose/5"
        : "border-line bg-surface";
  return (
    <div className={cn("rounded-md border px-2.5 py-1.5", verdictTint)}>
      <div className="mb-0.5 flex items-center gap-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
          {label}
        </span>
        <Badge
          variant={
            result.status === "compliant"
              ? "ok"
              : result.status === "non-compliant"
                ? "err"
                : "neutral"
          }
          size="sm"
        >
          {result.status}
        </Badge>
      </div>
      {result.errors.length > 0 ? (
        <ul className="m-0 list-disc pl-5 text-[10.5px] text-fg-dim">
          {result.errors.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      ) : null}
      <div className="mt-1 grid grid-cols-2 gap-1 font-mono text-[10px]">
        <div>
          <span className="text-fg-mute">desired:</span>{" "}
          <span className="text-fg-dim">{compact(result.desired)}</span>
        </div>
        <div>
          <span className="text-fg-mute">current:</span>{" "}
          <span className="text-fg-dim">{compact(result.current)}</span>
        </div>
      </div>
    </div>
  );
}
